import type { NsDict } from '../ru';

export const lobby: NsDict<'lobby'> = {
  balance: 'Balance',
  atTables: 'At tables',
  openTables: 'Open tables',
  rulesBtn: 'Rules and payout calculation',

  flowTitle: 'How a game works',
  'flow.prep': 'Preparation',
  'flow.prepTextAnte': 'ante {ante|cr} into the pot, composition hash published',
  'flow.round1': 'Round 1 · chart',
  'flow.round1Text': 'no price scale; choose a direction',
  'flow.round2': 'Round 2 · scale',
  'flow.round2TextWr': 'prices and points; the direction can be changed',
  'flow.round3': 'Round 3 · composition',
  'flow.round3TextWr': 'three stocks revealed; number of points only',
  'flow.target': 'Target candle',
  'flow.targetText': 'P₁ — close of the next minute',
  'flow.settleWr': 'Settlement',
  'flow.settleTextWr': 'those who guessed the direction win; the share depends on accuracy',

  'cat.n10': 'Starter',
  'cat.n50': 'Standard',
  'cat.n200': 'High',
  notEnough: 'Not enough credits for the ante.',

  'room.playing': 'game in progress',
  'room.startIn': 'starts in {n} s',
  'room.settling': 'settlement',
  'room.ready': 'ready {ready} of {min}',
  'room.taken': 'taken {seated} of {max}',
  'room.open': 'Open',
  'room.watch': 'Watch',

  'wr.startIn': 'The game starts in {n} s',
  'wr.waitMore': 'Waiting for {n} more confirmation|Waiting for {n} more confirmations',
  'wr.allReady': 'Everyone is ready',
  'wr.preparing': 'Preparing the next game',
  'wr.summary': 'Table {ante|cr} · {min}–{max} players · seated {seated}, ready {ready}',
  'wr.botsNote': 'If there are not enough people, bots will fill the table (they are marked).',
  'wr.spectating': 'You are watching this table.',
  'wr.confirmed': '✓ Participation confirmed.',
  'wr.anteOnStart': 'The ante of {ante|cr} will be charged at the start.',
  'wr.cancel': 'Cancel participation',
  'wr.join': 'Join · ante {ante|cr}',
  'wr.yourBalance': 'Your balance {amount|cr}',
  'wr.seats': 'Seats at the table',
  'wr.you': 'you',
  'wr.gamesCount': 'games: {n}',
  'wr.winRateShort': 'WR',
  'wr.offline': 'offline',
  'wr.ready': 'ready',
  'wr.waiting': 'waiting',
  'wr.free': 'free',

  'rules.title': 'Market Table rules',
  'rules.demo': 'A demo with play credits. There is no real money, no payments, no buying or withdrawing of credits. This is not trading advice.',
  'rules.indexTitle': 'The game index',
  'rules.indexText':
    'Before every game the server randomly picks three different stocks from an open set of liquid S&P 500 stocks. The index is an equally weighted combination of their percentage changes relative to the close of bar P₀:',
  'rules.index1': 'Bᵢ — the stock’s close on bar P₀; the price step is {tick} points; rounding to the nearest step, halves round up.',
  'rules.index2': 'Bar O/C — the index from opens/closes; H/L — from the components’ highs/lows (upper/lower estimate), but never narrower than O and C.',
  'rules.index3': 'A minute without trades in a stock uses its last price.',
  'rules.index4': '“Activity” — the minute volume averaged over the three stocks, divided by the stock’s average volume in the history window (1 = normal). VWAP* is weighted by activity.',
  'rules.index5': 'The formula is public; only the composition of the three is hidden until the third round. The hash commitment is published before betting and revealed after settlement.',
  'rules.flowTitle': 'Course of a game',
  'rules.flow1': 'The ante N goes into the pot, P₀ is fixed.',
  'rules.flow2': 'Round 1: the chart without a scale, choice of direction (up/down) and betting.',
  'rules.flow3': 'Round 2: the price scale and distances in points are revealed. The direction can be kept or changed.',
  'rules.flow4': 'Round 3: the composition is revealed. The direction can no longer change — enter a strictly positive number of points (step {tick}).',
  'rules.flow5': 'Lock-in. P₁ is the close of the next one-minute candle; Δ = P₁ − P₀.',
  'rules.actionsTitle': 'Actions',
  'rules.actionsText':
    'Fold, check, bet, call, raise (at most one raise after the first bet in a round), all-in (with side pots). Time per turn is {s} s; on timeout — check if possible, otherwise fold. At the end of a round only a player whose turn timed out folds. A player who did not get a turn stays in the game: the uncalled part of the bet is returned. A participant without a complete forecast at lock-in is eliminated.',
  'rules.payoutTitle': 'Winners and payouts',
  'rules.payout1': 'A wrong direction always loses, even with an exact number of points.',
  'rules.payout2':
    'For those who guessed: error = |forecast − |Δ||; weight = contribution_to_pot / (1 + error / {tick}). The pot is split in proportion to the weights; remainders of 0.01 cr go by the largest remainder method, ties by turn order.',
  'rules.payout3': 'Each side pot is settled separately; only those who contributed to it and did not fold are eligible. A pot with no correct guesses is returned to its contributors.',
  'rules.payout4': 'Cancellation with a full refund and zero commission: P₁ = P₀; none of the remaining players guessed the direction; no reliable data.',
  'rules.payout5': 'If everyone but one folds, the remaining player takes the pot without a forecast check.',
  'rules.exampleTitle': 'Example',
  'rules.exampleText':
    'Four players, ante 10 cr; Δ = +2.00. Player A: “up 2.00” (error 0, weight 10), B: “up 2.25” (error 0.25 = 1 step, weight 10/2 = 5), C and D — “down”. Pot 40 cr, commission 5% = 2 cr; 38 cr to distribute: A — 25.33 cr, B — 12.67 cr.',
  'rules.commissionTitle': 'Commission',
  'rules.commissionText':
    '{pct}% of the new credits put into the pot in each round (in the first one, together with the antes). Credits put in earlier and returned are not charged; no commission is withheld on cancellation.',
  'rules.capOne': 'one ante',
  'rules.capMany': '{n} ante|{n} antes',
  'rules.cap': 'The cap is {antes} N per round, i.e. at most {max}N per game (like a rake cap in poker).',
  'rules.retained': 'The withheld amount goes to a separate house account of the demo.',
  'rules.hotkeysTitle': 'Keyboard shortcuts',
  'rules.hotkeys': '↑/↓ direction · +/− points (Shift ×4) · F fold · C check/call · B bet/raise · A all-in · Enter confirm · Esc cancel',
  // ── режимы игры (0.8.0) ──
  'pickTitle': 'Choose a game',
  'pickIntro': 'Two modes, two characters. Pick a mode and a table: 4 to 8 players, each game takes three minutes.',
  'variant.blind3.title': 'Blind Three',
  'variant.blind3.tag': '3 of 500',
  'variant.blind3.text': 'Three random S&P 500 stocks, names hidden until the last round. Outside analysis tools won\'t help: everyone at the table is on equal terms — chart reading and nerve decide.',
  'variant.blind3.badge': 'level field',
  'variant.blind3.f1': 'composition hidden until round 3',
  'variant.blind3.f2': 'direction in rounds 1–2',
  'variant.spx.title': 'Open Cards',
  'variant.spx.tag': 'S&P 500',
  'variant.spx.text': 'The instrument is known in advance and the real price is visible from the first second. Use all your analysis and your own tools. A mode for the confident.',
  'variant.spx.badge': 'for the confident',
  'variant.spx.f1': 'instrument {symbol}, price visible at once',
  'variant.spx.f2': 'direction in round 1 only',
  'variant.spx.demoNote': 'Demo: SPY fund quotes from a random moment of recent days.',
  'tier.ante': 'ante',
  'tier.seated': 'seated: {n}',
  'tier.empty': 'open',
  'tier.mine': 'you are here',
  'tier.aria': 'Take a seat at the {ante|cr} table · {variant}',
  'chooseTable': 'Choose a table',
  'liveTitle': 'Tables now',
  'liveEmpty': 'No table is playing yet — pick a mode and a table above.',
  'flow.open.prepText': '{ante|cr} ante into the pot; instrument and price visible at once',
  'flow.open.round1': 'Round 1 · direction',
  'flow.open.round1Text': 'choose the direction; it is fixed after the round',
  'flow.open.round2': 'Round 2 · points',
  'flow.open.round2Text': 'number of points only',
  'flow.open.round3': 'Round 3 · points',
  'flow.open.round3Text': 'last refinement of the points',
  'rules.variantsTitle': 'Game modes',
  'rules.variantBlind3': 'Blind Three (3 of 500): an index of three random stocks; the price scale is hidden until round 2 and the composition until round 3. The direction is chosen in preparation and rounds 1–2.',
  'rules.variantSpx': 'Open Cards (S&P 500): a known instrument and its real price in dollars with a 0.01 step; chart and price are visible from the start. The direction is chosen in preparation and round 1; in rounds 2–3 only the number of points changes (1 point = $1). In the demo it is the SPY fund at a random moment of recent days. Betting, commission and settlement are shared by both modes.',
  // ── режимы игры (0.8.0) ──
  // ── подпись «демо» и карточки «скоро» (0.8.1) ──
  'variant.demo': 'demo',
  'soon.label': 'coming soon',
  'soon.sub': 'for dollars · coming soon',
  'soon.tier': 'soon',
  'soon.toast': 'Dollar tables are coming soon.',
  'soon.aria': '{amount} $ table · {variant} — coming soon',
};
